import { useState } from 'react';
import { ArrowLeft, Check, CircleCheck, CircleSlash, History, Layers, Sparkles, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useSOPDecisionHistory } from '@/hooks';
import type { SOPDecisionEntry, SOPDecisionRun, SOPDecisionSource, SOPGenerationReport } from '@/types';
import { ThoughtTimeline } from './thought-timeline';
import { PanelHeader } from './panel-header';
import { CollapsibleSection } from '@/modules/simulator/sidebar/panel';
import { Chip } from '@/modules/workflows/components/seg-node-inspector';

/**
 * Decision history — why the generated workflow looks the way it does.
 *
 * Generation is mostly unattended, so the operator needs to be able to back-track:
 * what was asked, what the answer was, and crucially *who or what* answered it —
 * a person, an assumption made on publish, or a choice carried over from a previous
 * version. Runs are listed newest first.
 *
 * Docked beside the SOP rather than sliding over it, for the same reason the generation
 * panel is: the history is read *against* the document, and a drawer covered the thing it
 * was explaining.
 */

const SOURCE_META: Record<SOPDecisionSource, { label: string; icon: typeof User; className: string }> = {
	human: { label: 'Answered by you', icon: User, className: 'bg-accent-purple/10 text-accent-purple' },
	auto: { label: 'Assumed on publish', icon: Sparkles, className: 'bg-amber-50 text-amber-700' },
	reused: { label: 'Carried over', icon: History, className: 'bg-gray-200 text-gray-900' }
};

function SourceBadge({ source }: { source: SOPDecisionSource }) {
	const meta = SOURCE_META[source] ?? SOURCE_META.human;
	const Icon = meta.icon;
	return (
		<span className={`flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] leading-4 ${meta.className}`}>
			<Icon className='size-3' />
			{meta.label}
		</span>
	);
}

function formatWhen(iso: string): string {
	const date = new Date(iso);
	return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
}

/** Soft-cap for legacy sessions that logged a full SOP dump as the question. */
const QUESTION_DISPLAY_MAX = 280;

function displayQuestion(question: string): string {
	const collapsed = question.replace(/\s+/g, ' ').trim();
	if (collapsed.length <= QUESTION_DISPLAY_MAX) return collapsed;
	return `${collapsed.slice(0, QUESTION_DISPLAY_MAX - 1).trimEnd()}…`;
}

function Entry({ entry }: { entry: SOPDecisionEntry }) {
	return (
		<li className='flex flex-col gap-1 border-t border-gray-500 px-3 py-2.5 first:border-t-0'>
			<p className='text-xs leading-4 text-gray-800'>{displayQuestion(entry.question)}</p>
			<div className='flex flex-wrap items-center gap-2'>
				<span className='text-[13px] leading-4.5 text-text-body'>{entry.answerLabel}</span>
				<SourceBadge source={entry.source} />
			</div>
			{entry.answeredAt && <p className='text-[11px] leading-4 tabular-nums text-gray-700'>{formatWhen(entry.answeredAt)}</p>}
		</li>
	);
}

/** `150000` → `2m 30s` — how long the operator waited, in the units they think in. */
function formatDuration(ms: number): string {
	const seconds = Math.round(ms / 1000);
	if (seconds < 60) return `${seconds}s`;
	const minutes = Math.floor(seconds / 60);
	return `${minutes}m ${seconds % 60}s`;
}

/** A collapsible section of a run. Open by default only for the run being read. */
function Section({
	title,
	count,
	defaultOpen,
	children
}: {
	title: string;
	count?: number;
	defaultOpen?: boolean;
	children: React.ReactNode;
}) {
	const [open, setOpen] = useState(defaultOpen ?? false);
	return (
		<CollapsibleSection
			icon={Layers}
			title={count !== undefined ? `${title} · ${count}` : title}
			open={open}
			onOpenChange={setOpen}
			className='rounded-xl border border-gray-500 bg-white last:border-b'
		>
			{children}
		</CollapsibleSection>
	);
}

/** One fact about the run, as a pill: `20 steps`, `8 tools`, `2m 30s`. */
function Stat({ children }: { children: React.ReactNode }) {
	return <Chip className='font-sans'>{children}</Chip>;
}

/**
 * What the run produced.
 *
 * Ordered by what somebody comes back for: the headline numbers, then whether the checks
 * passed, then what the generator had to fix or leave out — the two things nothing else
 * records — and the judge's reasoning last.
 */
function Report({ report }: { report: SOPGenerationReport }) {
	return (
		<div className='flex flex-col gap-3'>
			<div className='flex items-center gap-3'>
				{report.score && (
					<div className='flex shrink-0 flex-col items-center justify-center rounded-xl bg-accent-purple/10 px-3.5 py-2 text-accent-purple'>
						<span className='text-lg leading-5 font-semibold'>{report.score.grade}</span>
						<span className='text-[11px] leading-4 tabular-nums'>{report.score.score}</span>
					</div>
				)}
				<div className='flex flex-wrap items-center gap-1.5'>
					{report.nodes !== undefined && <Stat>{report.nodes} steps</Stat>}
					{report.tools && report.tools.length > 0 && <Stat>{report.tools.length} tools</Stat>}
					<Stat>{formatDuration(report.durationMs)}</Stat>
					{report.revision != null && <Stat>rev {report.revision}</Stat>}
				</div>
			</div>

			{report.failure && (
				<p className='rounded-xl border border-red-500/40 bg-red-50 p-3 text-xs leading-4.5 text-gray-900'>{report.failure.message}</p>
			)}

			{report.tools && report.tools.length > 0 && (
				<div className='flex flex-wrap gap-1.5'>
					{report.tools.map((tool) => (
						<span
							key={`${tool.provider ?? ''}-${tool.tool}`}
							className='rounded-md bg-gray-200 px-1.5 py-0.5 font-mono text-[11px] leading-4 text-gray-900'
						>
							{tool.provider ? `${tool.provider} · ${tool.tool}` : tool.tool}
						</span>
					))}
				</div>
			)}

			{report.gates && report.gates.length > 0 && (
				<ul className='flex flex-col gap-1'>
					{report.gates.map((gate) => (
						<li
							key={gate.name}
							className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-xs leading-4 ${
								gate.passed ? 'border-gray-500 bg-white' : 'border-amber-300 bg-amber-50'
							}`}
						>
							{gate.passed ? (
								<Check className='mt-0.5 size-3 shrink-0 text-emerald-500' strokeWidth={3} />
							) : (
								<CircleSlash className='mt-0.5 size-3 shrink-0 text-amber-700' />
							)}
							<span className='text-gray-900'>
								{GATE_LABEL[gate.name] ?? gate.name}
								{!gate.passed && gate.issues.length > 0 && (
									<span className='text-amber-700'> — {gate.issues.join('; ')}</span>
								)}
							</span>
						</li>
					))}
				</ul>
			)}

			{report.repairs && report.repairs.length > 0 && (
				<div>
					<p className='text-xs leading-4 text-gray-800'>Fixed in the draft</p>
					<ul className='mt-1 flex flex-col gap-1'>
						{report.repairs.map((repair, index) => (
							<li key={`${repair.stepId ?? repair.kind}-${index}`} className='text-[12px] leading-4 text-gray-900'>
								{repair.detail}
							</li>
						))}
					</ul>
				</div>
			)}

			{report.pulledOut && report.pulledOut.length > 0 && (
				<div>
					<p className='text-xs leading-4 text-gray-800'>Handled in code, not in the graph</p>
					<p className='mt-1 text-[12px] text-gray-900'>{report.pulledOut.join(', ')}</p>
				</div>
			)}

			{report.questions && report.questions.asked > 0 && (
				<p className='text-[12px] text-gray-900'>
					{report.questions.asked} question(s): {report.questions.byHuman} answered by you,{' '}
					{report.questions.assumed} assumed, {report.questions.reused} carried over.
				</p>
			)}

			{report.judge && report.judge.length > 0 && (
				<div>
					<p className='text-xs leading-4 text-gray-800'>How it was judged</p>
					<ul className='mt-1 flex flex-col gap-1.5'>
						{report.judge.map((verdict) => (
							<li key={verdict.dimension} className='text-[12px] leading-4'>
								<span className='font-medium text-gray-900'>
									{verdict.dimension} · {verdict.score}/4
								</span>
								{verdict.reasoning && <span className='text-gray-900'> — {verdict.reasoning}</span>}
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
}

/** Gate names in the operator's terms — "coverage" means nothing to the person reading. */
const GATE_LABEL: Record<string, string> = {
	coverage: 'Nothing dropped from the protocol',
	'response-fidelity': 'Replies intact, word for word',
	'tool-uniqueness': 'No tool called twice for the same thing'
};

function Run({ run, index, total }: { run: SOPDecisionRun; index: number; total: number }) {
	const isCurrent = index === 0;
	const failed = run.status === 'failed';
	const progress = run.progress ?? [];

	return (
		<section className='flex flex-col gap-3 border-b border-gray-500 p-4 last:border-b-0'>
			<div className='flex flex-wrap items-center gap-2'>
				<p className='text-[13px] leading-4.5 font-medium text-text-heading'>
					{isCurrent ? 'Latest generation' : `Generation ${total - index}`}
				</p>
				{failed ? (
					<span className='flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] leading-4 font-medium text-red-500'>
						<CircleSlash className='size-3' />
						Failed
					</span>
				) : (
					isCurrent && (
						<span className='flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] leading-4 font-medium text-green-500'>
							<CircleCheck className='size-3' />
							In use
						</span>
					)
				)}
				{run.startedAt && <span className='text-[11px] leading-4 tabular-nums text-gray-700'>{formatWhen(run.startedAt)}</span>}
			</div>

			{failed && run.failureReason && (
				<p className='rounded-xl border border-red-500/40 bg-red-50 p-3 text-xs leading-4.5 text-gray-900'>{run.failureReason}</p>
			)}

			{run.report && (
				<Section title='What it produced' defaultOpen={isCurrent}>
					<Report report={run.report} />
				</Section>
			)}

			{progress.length > 0 && (
				<Section title='Chain of thought' count={progress.length}>
					<ThoughtTimeline activity={progress} />
				</Section>
			)}

			<Section title='Questions & answers' count={run.entries.length} defaultOpen={isCurrent}>
				{run.entries.length === 0 ? (
					<p className='text-[13px] text-gray-900'>No questions were needed — the protocol was unambiguous.</p>
				) : (
					<ul className='flex flex-col rounded-xl border border-gray-500'>
						{run.entries.map((entry) => (
							<Entry key={`${run.sessionId}-${entry.decisionId}`} entry={entry} />
						))}
					</ul>
				)}
			</Section>
		</section>
	);
}

interface DecisionHistoryPanelProps {
	sopId: string;
	open: boolean;
	onClose: () => void;
	/** Shown as "Back" when the history was opened from the compile panel. */
	onBack?: () => void;
}

export function DecisionHistoryPanel({ sopId, open, onClose, onBack }: DecisionHistoryPanelProps) {
	// Only fetches while the panel is open.
	const { data, isLoading, isError } = useSOPDecisionHistory(sopId, open);
	const runs = data?.runs ?? [];

	if (!open) return null;

	return (
		<aside className='flex h-screen w-[420px] shrink-0 flex-col overflow-hidden border-l border-gray-500 bg-white'>
			<PanelHeader
				title='Compile history'
				status='Every question a compile asked, and how it was answered.'
				onClose={onClose}
				actions={
					onBack && (
						<Button variant='ghost' size='sm' className='h-7 gap-1 px-2 text-xs' onClick={onBack}>
							<ArrowLeft className='size-3.5' />
							Back
						</Button>
					)
				}
			/>

			<ScrollArea className='min-h-0 flex-1'>
				<div>
					{isLoading ? (
						<div className='flex flex-col gap-3 p-4'>
							<Skeleton className='h-4 w-32' />
							<Skeleton className='h-20 w-full rounded-xl' />
							<Skeleton className='h-20 w-full rounded-xl' />
						</div>
					) : isError ? (
						<p className='p-4 text-[13px] leading-[18px] text-gray-900'>Couldn&apos;t load the history. Try reopening this panel.</p>
					) : runs.length === 0 ? (
						<p className='p-4 text-[13px] leading-[18px] text-gray-900'>
							No compiles yet. Each compile's questions and answers appear here.
						</p>
					) : (
						<div className='flex flex-col bg-gray-50'>
							{runs.map((run, index) => (
								<Run key={run.sessionId} run={run} index={index} total={runs.length} />
							))}
						</div>
					)}
				</div>
			</ScrollArea>
		</aside>
	);
}
